import type { Dispatch } from 'react';
import { Checkbox, Space, Tag, Tooltip, Typography } from 'antd';
import { space } from '@viq-admin/ui';
import { CRM_COPY } from './copy';
import { HelpHint } from './HelpHint';
import { failureSummary } from '../../leadRules/labels';
import { LEAD_RULES_COPY } from '../../leadRules/copy';
import { CRM_STAGE_STRIP_HEIGHT } from '../types';
import type { PersonPreview } from './types';
import type { CrmQueueAction } from './useCrmQueue';

export interface SendReadyListProps {
  readyRows: PersonPreview[];
  deselected: Record<string, true>;
  selectedCount: number;
  allSelected: boolean;
  someSelected: boolean;
  canManage: boolean;
  /** Optional, mirroring `SendViewProps.loading` — its caller's own contract. */
  loading?: boolean;
  dispatch: Dispatch<CrmQueueAction>;
}

/**
 * The Send tab's "ready to send" list — header with select-all, the
 * still-checking line, and one row per ready lead. Pure presentation: every
 * value it renders is already computed by `SendView` (or its future
 * replacement) and handed down as a prop.
 */
export function SendReadyList({
  readyRows,
  deselected,
  selectedCount,
  allSelected,
  someSelected,
  canManage,
  loading = false,
  dispatch,
}: SendReadyListProps) {
  return (
    <>
      {/* Every section header keeps its count in its own text node — each help
          trigger is a SIBLING of the Title, never a child of it. */}
      <Space size={space.xs} align="center" style={{ marginBottom: space.sm }}>
        {/* `tabIndex={-1}` + the testid make this a stable, always-present
            focus target: `SendView`'s "Back to the list" button unmounts on
            click, and without somewhere deliberate to land, focus falls to
            `document.body` and the next Tab restarts at the top of the page
            (fix round, Minor a11y). Not naturally focusable on its own —
            `tabIndex={-1}` allows a *programmatic* `.focus()` without adding
            it to the page's own Tab order. */}
        <Typography.Title level={5} style={{ margin: 0 }} tabIndex={-1} data-testid="send-ready-heading">
          {`${CRM_COPY.readySectionHeader} (${selectedCount}/${readyRows.length})`}
        </Typography.Title>
        <HelpHint {...CRM_COPY.help.sendReadyGroup} testId="crm-help-send-ready" />
        {/* Selection defaults to every ready row, so this exists to opt OUT
            in bulk — clicking 50 boxes to hold a batch back was the whole
            complaint. Scoped to `readyRows`, which under a `changed` scope is
            the delta subset, so it never reaches a row the operator cannot
            currently see. */}
        <Checkbox
          data-testid="send-select-all"
          checked={allSelected}
          indeterminate={someSelected}
          disabled={!canManage || readyRows.length === 0}
          onChange={(e) =>
            dispatch({
              type: 'setAllSelected',
              personIds: readyRows.map((p) => p.personId),
              selected: e.target.checked,
            })
          }
          aria-label={CRM_COPY.selectAllReady}
        >
          <Typography.Text type="secondary">{CRM_COPY.selectAllReady}</Typography.Text>
        </Checkbox>
      </Space>
      {/* Only the ROWS scroll. The header and Select all above stay put, so
          the count and the bulk toggle are reachable at any scroll position.

          The window itself never scrolls: `BatchScreen` puts this view inside
          a `height: calc(100vh - 112px)` column whose inner
          `overflowY: 'auto'` div is what actually scrolls. So this region is a
          SECOND scroll surface nested in that one, and the 420px subtracted
          below is not viewport chrome — it is the shell's own 112px plus the
          event header, the KPI tiles, the stage strip and the hint line that
          sit above this list inside that scrolling container. Tune it against
          those, not against the browser's chrome.

          `max(240px, …)` is the floor, and it is load-bearing: a plain calc
          has none, so on a short viewport the list would cap at a handful of
          rows and below 420px of height CSS would clamp `max-height` to 0 —
          a header, a Select all and no list at all.

          `paddingRight` keeps the scrollbar off the checkboxes. No media
          query and no antd responsive map: a partial map lets antd's own
          defaults win above its largest declared breakpoint, and the jsdom
          `matchMedia` stub answers every query identically, so that bug
          class would test green. */}
      <div
        data-testid="send-ready-scroll"
        // Reachable and named for a keyboard user (fix round, Minor a11y): a
        // plain scrolling div is invisible to assistive tech and untabbable
        // on its own. `role="region"` + a name make it a landmark; the name
        // reuses `readySectionHeader` (the header just above it) rather than
        // inventing a near-duplicate string.
        role="region"
        aria-label={CRM_COPY.readySectionHeader}
        tabIndex={0}
        style={{
          maxHeight: 'max(240px, calc(100vh - 420px))',
          overflowY: 'auto',
          paddingRight: space.xs,
          // On the wrapper, not on the Space inside: a bottom margin within a
          // scroll region pads its scrolled content instead of spacing the
          // list from whatever follows it.
          marginBottom: space.lg,
        }}
      >
        <Space direction="vertical" size={space.sm} style={{ width: '100%' }}>
          {/* Said where the rows themselves will appear — not as an overlay, so
              the policy line, the summary disclosures and any results below
              stay readable and usable while the look-up finishes. */}
          {loading && readyRows.length === 0 && (
            <Typography.Text type="secondary" data-testid="crm-send-checking">
              {CRM_COPY.previewLoadingLine}
            </Typography.Text>
          )}
          {readyRows.map((p) => (
            // The testid moves to this wrapper, not the Checkbox itself: antd's
            // Checkbox forwards unrecognized props (data-testid included) onto
            // the native <input>, which — being a void element — can never
            // contain the amber tag rendered beside it. Wrapping is what lets
            // `within(getByTestId(...))` reach both.
            <div
              key={p.personId}
              data-testid={`send-ready-row-${p.personId}`}
              // Browsers ignore a `position: sticky` overlay when scrolling an
              // element into view (e.g. on Tab), so a row near the top of this
              // region can land behind `BatchScreen`'s pinned
              // `CRM_STAGE_STRIP_HEIGHT`-tall stage strip. `scrollMarginTop`
              // tells the browser to leave that much room above the row when
              // it scrolls it into view (fix round, Minor a11y).
              style={{ scrollMarginTop: CRM_STAGE_STRIP_HEIGHT }}
            >
              <Checkbox
                checked={!deselected[p.personId]}
                disabled={!canManage}
                onChange={() => dispatch({ type: 'toggleSelected', personId: p.personId })}
                aria-label={`Include ${p.displayName}`}
              >
                {p.displayName}
                {/* Deliberately no crmSentAt check here, unlike every other
                    surface's never-flag-a-sent-lead rule (isIncompleteUnsent,
                    used by BatchTable and LeadDetailBody): PersonPreview
                    carries no crmSentAt at all, and this whole view exists for
                    "about to send" — a previously-sent lead being re-sent can
                    still show Incomplete, which reads as right for a screen
                    whose only question is whether THIS send should go out. */}
                {p.completeness?.complete === false && (
                  <Tooltip title={failureSummary(p.completeness.failures)}>
                    <Tag color="gold" style={{ marginLeft: space.xs }}>
                      {LEAD_RULES_COPY.incompleteTag}
                    </Tag>
                  </Tooltip>
                )}
              </Checkbox>
            </div>
          ))}
        </Space>
      </div>
    </>
  );
}
